import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_without_properties } from "@swc/helpers/_/_object_without_properties";
import { all, put, select, takeEvery } from 'redux-saga/effects';
import ddt from '@wix/da-ddt';
import { fetchAll, fetchNext, fetchPrevious, fetchRetry, initializeIfNeeded } from './actions';
import { canStreamAutoFetch, getStream, isInitialized } from './selectors';
import { FetchType } from './types';
import { createCallbackActionCreator, getActionsForStreamType } from '../helpers';
function getFetchActionForType(actions, fetchType) {
    switch(fetchType){
        case FetchType.All:
            return actions.fetchAll;
        case FetchType.Previous:
            return actions.fetchPrevious;
        case FetchType.Next:
        default:
            return actions.fetch;
    }
}
function getFetchActionForStreamType(type, fetchType) {
    const actions = getActionsForStreamType(type);
    const action = getFetchActionForType(actions, fetchType);
    if (!action) {
        ddt.warn('stream', 'Unknown stream type or action', type, fetchType);
        return (streamId)=>({
                streamId,
                type: 'STREAM__UNKNOWN_TYPE__FETCH'
            }); // no idea what this type is, noop
    }
    return action;
}
function* handleFetchNext({ payload: { streamId } }) {
    const stream = yield select(getStream, streamId);
    if (!stream || !stream.hasMore || stream.errorMsg || !stream.streamType) {
        return;
    }
    const fetchAction = getFetchActionForStreamType(stream.streamType, FetchType.Next);
    // kick off the reducers and any legacy handlers
    yield put(fetchAction(streamId));
    if (stream.fetchNextCallback) {
        yield put(createCallbackActionCreator(stream.fetchNextCallback)(streamId));
    }
}
function* handleFetchPrev({ payload: { streamId } }) {
    const stream = yield select(getStream, streamId);
    if (!stream || !stream.hasLess || stream.errorMsg || !stream.streamType) {
        return;
    }
    const fetchAction = getFetchActionForStreamType(stream.streamType, FetchType.Previous);
    // kick off the reducers and any legacy handlers
    yield put(fetchAction(streamId));
    if (stream.fetchPreviousCallback) {
        yield put(createCallbackActionCreator(stream.fetchPreviousCallback)(streamId));
    }
}
function* handleFetchAll({ payload: { streamId } }) {
    const stream = yield select(getStream, streamId);
    if (!stream || !(stream.hasMore || stream.hasLess) || stream.errorMsg || !stream.streamType) {
        return;
    }
    const fetchAction = getFetchActionForStreamType(stream.streamType, FetchType.All);
    // kick off the reducers and any legacy handlers
    yield put(fetchAction(streamId));
    if (stream.fetchAllCallback) {
        yield put(createCallbackActionCreator(stream.fetchAllCallback)(streamId));
    }
}
function* handleInitIfNeeded(_param) {
    var { payload: { streamType, autoFetch } } = _param, initParams = _object_without_properties(_param.payload, [
        "streamType",
        "autoFetch"
    ]);
    const actions = getActionsForStreamType(streamType);
    const isInit = yield select(isInitialized, initParams.streamId);
    if (!isInit && actions.initialize) {
        yield put(actions.initialize(_object_spread({
            items: [],
            hasMore: true
        }, initParams)));
    }
    const canAutoFetch = yield select(canStreamAutoFetch, initParams.streamId);
    if (autoFetch && canAutoFetch) {
        yield put(fetchNext(initParams.streamId));
    }
}
function* handleFetchRetry(action) {
    const { streamId, failureFetchType } = yield select(getStream, action.payload.streamId);
    switch(failureFetchType){
        case FetchType.Previous:
            yield put(fetchPrevious(streamId));
            break;
        case FetchType.All:
            yield put(fetchAll(streamId));
            break;
        case FetchType.Next:
        default:
            yield put(fetchNext(streamId));
            break;
    }
}
export default function* baseStreamSaga(action) {
    yield all([
        takeEvery(fetchNext, handleFetchNext),
        takeEvery(fetchPrevious, handleFetchPrev),
        takeEvery(fetchAll, handleFetchAll),
        takeEvery(fetchRetry, handleFetchRetry),
        takeEvery(initializeIfNeeded, handleInitIfNeeded)
    ]);
}
